// Store reactiv pentru sursa de date a dashboard-ului.
//
// SURSA poate fi:
//   - "demo"     → JSON-urile statice generate din mock (fișierele din acest folder)
//   - "imported" → date încărcate de utilizator din Excel/CSV (WinMentor / Optima),
//                  persistate în localStorage ca să reziste la refresh.
//
// Componentele din src/components/* NU știu de acest store — ele consumă
// exclusiv getterele din `dataSource.ts`, care citesc starea curentă de aici.
// La orice schimbare, `subscribe` notifică App-ul, care re-desenează dashboard-ul.

import { useSyncExternalStore } from "react";
import type {
  ComandaRow,
  CostProductieRow,
  ExportSplitRow,
  ExportTrendRow,
  IncarcareMasinaRow,
  MediaMobilaRow,
  PierdereRow,
  ProductivitateOperatorRow,
  ProfitabilitateRow,
} from "@/types";

import costProductieJson from "./01_cost_productie.json";
import mediaMobilaJson from "./02_media_mobila.json";
import profitabilitateJson from "./03_profitabilitate.json";
import incarcareMasiniJson from "./04a_incarcare_masini.json";
import productivitateOperatoriJson from "./04b_productivitate_operatori.json";
import exportSplitJson from "./05a_export_split.json";
import exportTrendJson from "./05b_export_trend.json";
import comenziJson from "./06_comenzi.json";
import pierderiJson from "./07_pierderi.json";

export type DatasetKey =
  | "cost_productie"
  | "media_mobila"
  | "profitabilitate"
  | "incarcare_masini"
  | "productivitate_operatori"
  | "export_split"
  | "export_trend"
  | "comenzi"
  | "pierderi";

export interface DatasetBundle {
  cost_productie: CostProductieRow[];
  media_mobila: MediaMobilaRow[];
  profitabilitate: ProfitabilitateRow[];
  incarcare_masini: IncarcareMasinaRow[];
  productivitate_operatori: ProductivitateOperatorRow[];
  export_split: ExportSplitRow[];
  export_trend: ExportTrendRow[];
  comenzi: ComandaRow[];
  pierderi: PierdereRow[];
}

const DEMO: DatasetBundle = {
  cost_productie: costProductieJson as CostProductieRow[],
  media_mobila: mediaMobilaJson as MediaMobilaRow[],
  profitabilitate: profitabilitateJson as ProfitabilitateRow[],
  incarcare_masini: incarcareMasiniJson as IncarcareMasinaRow[],
  productivitate_operatori: productivitateOperatoriJson as ProductivitateOperatorRow[],
  export_split: exportSplitJson as ExportSplitRow[],
  export_trend: exportTrendJson as ExportTrendRow[],
  comenzi: comenziJson as ComandaRow[],
  pierderi: pierderiJson as PierdereRow[],
};

export function demoBundle(): DatasetBundle {
  return DEMO;
}

// ─── Derivare media mobilă din costul /oră lunar ─────────────────────────────
// Când clientul importă DOAR "cost producție", calculăm automat mediile mobile
// (1/6/12 luni) — clientul nu trebuie să le calculeze manual din WinMentor.
export function computeMediaMobila(cost: CostProductieRow[]): MediaMobilaRow[] {
  const sortate = [...cost].sort((a, b) => a.luna_key.localeCompare(b.luna_key));
  const r1 = (n: number) => Math.round(n * 10) / 10;
  const r2 = (n: number) => Math.round(n * 100) / 100;
  return sortate.map((row, i) => {
    const fereastra = (n: number) =>
      sortate.slice(Math.max(0, i - n + 1), i + 1).map((r) => r.cost_pe_ora_eur);
    const medie = (arr: number[]) => arr.reduce((s, v) => s + v, 0) / arr.length;
    const cur = row.cost_pe_ora_eur;
    const ma6 = medie(fereastra(6));
    const ma12 = medie(fereastra(12));
    return {
      luna_key: row.luna_key,
      cost_pe_ora_eur: r2(cur),
      ma_1l: r2(cur),
      ma_6l: r2(ma6),
      ma_12l: r2(ma12),
      semnal_vs_ma6: cur > ma6 ? "PESTE_MEDIE" : "SUB_MEDIE",
      deviatie_pct_ma6: ma6 > 0 ? r1(((cur - ma6) / ma6) * 100) : 0,
    };
  });
}

// ─── Starea persistabilă ─────────────────────────────────────────────────────
export type DataSourceMode = "demo" | "imported";

interface StoreState {
  mode: DataSourceMode;
  /** Override-uri per set de date; cele lipsă cad automat pe demo. */
  overrides: Partial<Record<DatasetKey, unknown[]>>;
  importedAt: string | null;
}

const STORAGE_KEY = "glass-bi-datasource-v1";

function load(): StoreState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { mode: "demo", overrides: {}, importedAt: null };
    const parsed = JSON.parse(raw) as StoreState;
    if (parsed && (parsed.mode === "demo" || parsed.mode === "imported")) {
      return {
        mode: parsed.mode,
        overrides: parsed.overrides ?? {},
        importedAt: parsed.importedAt ?? null,
      };
    }
  } catch {
    // localStorage indisponibil sau corupt — cădem pe demo, fără să crăpăm.
  }
  return { mode: "demo", overrides: {}, importedAt: null };
}

function persist(state: StoreState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // quota depășită / mod privat — ignorăm, datele rămân în memorie pe sesiune.
  }
}

let state: StoreState = load();
let version = 0;
const listeners = new Set<() => void>();

function emit() {
  version += 1;
  persist(state);
  listeners.forEach((l) => l());
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getVersion(): number {
  return version;
}

/** Hook simplu de re-render la orice schimbare a sursei de date. */
export function useDataVersion(): number {
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

// ─── Setări ──────────────────────────────────────────────────────────────────
export function applyImport(key: DatasetKey, rows: unknown[]) {
  state = {
    mode: "imported",
    overrides: { ...state.overrides, [key]: rows },
    importedAt: new Date().toISOString(),
  };
  emit();
}

export function clearDataset(key: DatasetKey) {
  const overrides = { ...state.overrides };
  delete overrides[key];
  const areImport = Object.keys(overrides).length > 0;
  state = {
    mode: areImport ? "imported" : "demo",
    overrides,
    importedAt: areImport ? state.importedAt : null,
  };
  emit();
}

export function resetToDemo() {
  state = { mode: "demo", overrides: {}, importedAt: null };
  emit();
}

// ─── Citire ──────────────────────────────────────────────────────────────────
export interface DataSourceInfo {
  mode: DataSourceMode;
  importedAt: string | null;
  importedKeys: DatasetKey[];
}

export function getInfo(): DataSourceInfo {
  return {
    mode: state.mode,
    importedAt: state.importedAt,
    importedKeys: Object.keys(state.overrides) as DatasetKey[],
  };
}

export function isImported(key: DatasetKey): boolean {
  return Boolean(state.overrides[key]);
}

/** Bundle-ul EFECTIV consumat de UI: override-uri unde există, altfel demo. */
export function effective(): DatasetBundle {
  if (state.mode !== "imported") return DEMO;
  const o = state.overrides;
  const cost = (o.cost_productie as CostProductieRow[] | undefined) ?? DEMO.cost_productie;

  // Media mobilă: override explicit > derivată din cost importat > demo.
  let media: MediaMobilaRow[];
  if (o.media_mobila) media = o.media_mobila as MediaMobilaRow[];
  else if (o.cost_productie) media = computeMediaMobila(cost);
  else media = DEMO.media_mobila;

  return {
    cost_productie: cost,
    media_mobila: media,
    profitabilitate: (o.profitabilitate as ProfitabilitateRow[] | undefined) ?? DEMO.profitabilitate,
    incarcare_masini:
      (o.incarcare_masini as IncarcareMasinaRow[] | undefined) ?? DEMO.incarcare_masini,
    productivitate_operatori:
      (o.productivitate_operatori as ProductivitateOperatorRow[] | undefined) ??
      DEMO.productivitate_operatori,
    export_split: (o.export_split as ExportSplitRow[] | undefined) ?? DEMO.export_split,
    export_trend: (o.export_trend as ExportTrendRow[] | undefined) ?? DEMO.export_trend,
    comenzi: (o.comenzi as ComandaRow[] | undefined) ?? DEMO.comenzi,
    pierderi: (o.pierderi as PierdereRow[] | undefined) ?? DEMO.pierderi,
  };
}
